import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { TrendingDown, Award, Info } from "lucide-react";
import { api, priceFmt } from "../lib/api";

function formatDate(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
}

function scoreAccent(score) {
  if (score >= 90) return { bg: "bg-emerald-500", text: "text-emerald-400", border: "border-emerald-500/40", glow: "shadow-[0_0_30px_rgba(16,185,129,0.25)]" };
  if (score >= 75) return { bg: "bg-[#FBD51C]", text: "text-[#FBD51C]", border: "border-[#FBD51C]/40", glow: "shadow-[0_0_30px_rgba(251,213,28,0.2)]" };
  if (score >= 60) return { bg: "bg-blue-500", text: "text-blue-400", border: "border-blue-500/40", glow: "" };
  if (score >= 40) return { bg: "bg-zinc-500", text: "text-zinc-400", border: "border-zinc-700", glow: "" };
  return { bg: "bg-zinc-700", text: "text-zinc-500", border: "border-zinc-800", glow: "" };
}

export default function PriceHistorySection({ deal }) {
  const dealId = deal?.id;
  const { data, isLoading } = useQuery({
    queryKey: ["price-history", dealId],
    queryFn: () =>
      api.get(`/deals/${dealId}/price-history`, { params: { days: 90 } }).then((r) => r.data),
    enabled: !!dealId,
    staleTime: 5 * 60_000,
  });

  if (isLoading || !data) return null;

  const history = data.history || [];
  const stats = data.stats || {};
  const score = data.deal_score;
  const scoreLabel = data.deal_score_label;
  const reasons = data.deal_score_reasons || [];
  const current = data.current_price;
  const belowAvg = stats.below_avg_pct;

  // chart data — map ts → shortDate
  const chartData = history.map((h) => ({
    date: formatDate(h.ts),
    ts: h.ts,
    price: h.price,
  }));

  const accent = scoreAccent(score || 0);

  return (
    <section
      className="mt-6 rounded-3xl bg-gradient-to-br from-zinc-950 to-zinc-900/40 border border-zinc-800 p-5 sm:p-6"
      data-testid="price-history-section"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown size={14} className="text-[#FBD51C]" />
            <span className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-[#FBD51C]">
              Analisi prezzo
            </span>
          </div>
          <h2 className="font-display font-black text-2xl text-white leading-tight">
            Andamento e valutazione
          </h2>
        </div>

        {/* Deal score badge */}
        {typeof score === "number" && (
          <div
            data-testid="deal-score-badge"
            className={`inline-flex items-center gap-3 rounded-2xl border ${accent.border} ${accent.glow} bg-black/40 px-4 py-3`}
          >
            <div className="flex flex-col items-center leading-none pr-3 border-r border-zinc-800">
              <div className={`font-display font-black text-3xl ${accent.text}`}>{score}</div>
              <div className="font-mono-bdd text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                Deal Score
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <Award size={13} className={accent.text} />
                <span className="text-sm font-bold text-white">{scoreLabel}</span>
              </div>
              <Link
                to="/come-valutiamo"
                data-testid="deal-score-methodology-link"
                className="text-[10px] text-zinc-500 hover:text-[#FBD51C] inline-flex items-center gap-1 mt-1"
              >
                <Info size={10} /> Come lo calcoliamo
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Stat pills */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6" data-testid="price-stats-pills">
        <StatPill
          label="Minimo storico"
          value={stats.min_ever ? priceFmt(stats.min_ever) : "—"}
          accent="text-emerald-400"
        />
        <StatPill
          label="Media 30gg"
          value={stats.avg_30d ? priceFmt(stats.avg_30d) : "—"}
          accent="text-zinc-300"
        />
        <StatPill
          label="Prezzo attuale"
          value={current ? priceFmt(current) : "—"}
          accent="text-[#FBD51C]"
          highlight
        />
        <StatPill
          label={belowAvg > 0 ? "Sotto la media" : "Rispetto alla media"}
          value={
            typeof belowAvg === "number"
              ? (belowAvg > 0 ? `-${belowAvg.toFixed(1)}%` : `+${Math.abs(belowAvg).toFixed(1)}%`)
              : "—"
          }
          accent={belowAvg > 0 ? "text-emerald-400" : "text-zinc-400"}
        />
      </div>

      {/* Chart */}
      <div className="rounded-2xl bg-black/30 border border-zinc-900 p-3">
        {chartData.length < 2 ? (
          <div className="h-40 flex flex-col items-center justify-center text-center px-6">
            <div className="text-sm text-zinc-400 font-bold mb-1">
              Storico prezzi in raccolta
            </div>
            <div className="text-xs text-zinc-600">
              Stiamo tracciando i cambi di prezzo su questa offerta. Il grafico
              apparirà nei prossimi giorni.
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FBD51C" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#FBD51C" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                stroke="#52525b"
                tick={{ fontSize: 10, fill: "#71717a" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                stroke="#52525b"
                tick={{ fontSize: 10, fill: "#71717a" }}
                axisLine={false}
                tickLine={false}
                domain={["dataMin - 5", "dataMax + 5"]}
                tickFormatter={(v) => `€${Math.round(v)}`}
                width={40}
              />
              <Tooltip
                contentStyle={{
                  background: "#0e1621",
                  border: "1px solid #27272a",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: "#a1a1aa" }}
                formatter={(v) => [`€${Number(v).toFixed(2)}`, "Prezzo"]}
              />
              {stats.avg_30d && (
                <ReferenceLine
                  y={stats.avg_30d}
                  stroke="#71717a"
                  strokeDasharray="4 4"
                  label={{
                    value: `Media ${priceFmt(stats.avg_30d)}`,
                    position: "insideTopRight",
                    fill: "#a1a1aa",
                    fontSize: 10,
                  }}
                />
              )}
              <Area
                type="monotone"
                dataKey="price"
                stroke="#FBD51C"
                strokeWidth={2}
                fill="url(#priceGrad)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Editorial verdict */}
      {stats.is_lowest_ever && stats.samples_total >= 3 && (
        <div className="mt-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-300">
          🎯 <b>Minimo storico:</b> è il prezzo più basso mai registrato per questa offerta da quando la tracciamo.
        </div>
      )}
      {!stats.is_lowest_ever && belowAvg > 5 && (
        <div className="mt-4 p-4 rounded-xl bg-[#FBD51C]/10 border border-[#FBD51C]/30 text-sm text-[#FBD51C]">
          ✅ Prezzo sotto la media degli ultimi 30 giorni del {belowAvg.toFixed(1)}%.
        </div>
      )}

      {reasons.length > 0 && (
        <div className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
          <span className="text-zinc-400 font-bold">Perché questo punteggio:</span>{" "}
          {reasons.join(" · ")}
        </div>
      )}
    </section>
  );
}

function StatPill({ label, value, accent, highlight }) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        highlight ? "bg-[#FBD51C]/5 border-[#FBD51C]/30" : "bg-zinc-900/50 border-zinc-800"
      }`}
    >
      <div className="font-mono-bdd text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
        {label}
      </div>
      <div className={`text-lg font-black leading-none ${accent}`}>{value}</div>
    </div>
  );
}
